"use client";

import dayjs from "dayjs";
import { useCallback, useMemo } from "react";
import { useQueryStates } from "nuqs";
import { rentalParsers } from "./shared/parsers";
import { getStableTodayIso, getStableTomorrowIso } from "@/lib/date";
import { buildQueryString } from "@/lib/utils";

export const useRentalQuery = () => {
  const [q, setQ] = useQueryStates(rentalParsers, { history: "replace" });

  const setLocationId = useCallback(
    (value: string) =>
      setQ({ locationId: value.trim() || null, lat: null, lng: null }),
    [setQ],
  );

  const setCoordinates = useCallback(
    (lat: number, lng: number) =>
      setQ({ locationId: null, lat: `${lat}`, lng: `${lng}` }),
    [setQ],
  );

  const setSearch = useCallback(
    (value: string) => setQ({ search: value.trim() || null }),
    [setQ],
  );

  const setRange = useCallback(
    (from?: Date, to?: Date) =>
      setQ({
        start: from ? dayjs(from).format("YYYY-MM-DD") : null,
        end: to ? dayjs(to).format("YYYY-MM-DD") : null,
      }),
    [setQ],
  );

  const setDate = useCallback(
    (date?: Date) =>
      setQ({
        start: date ? dayjs(date).format("YYYY-MM-DD") : null,
        end: null,
      }),
    [setQ],
  );

  const setVehicleType = useCallback(
    (value: string) => setQ({ vehicleType: value.trim() || null }),
    [setQ],
  );

  const setPickupType = useCallback(
    (value: string) => setQ({ pickupType: value.trim() || null }),
    [setQ],
  );

  const setFilters = useCallback(
    (updates: Parameters<typeof setQ>[0]) => {
      if (!updates || typeof updates === "function") {
        setQ(updates);
        return;
      }
      const cleaned = Object.fromEntries(
        Object.entries(updates as Record<string, unknown>).map(
          ([key, value]) => [
            key,
            Array.isArray(value) && value.length === 0 ? null : value,
          ],
        ),
      ) as Parameters<typeof setQ>[0];
      setQ(cleaned);
    },
    [setQ],
  );

  const clearSearch = useCallback(
    () =>
      setQ({
        locationId: null,
        search: null,
        lat: null,
        lng: null,
        start: null,
        end: null,
        vehicleType: null,
        pickupType: null,
      }),
    [setQ],
  );

  const clearFilters = useCallback(
    () =>
      setQ({
        priceRange: null,
        gear: null,
        engine: null,
        size: null,
        category: null,
        mustInclude: null,
        rating: null,
      }),
    [setQ],
  );

  const defaultToday = getStableTodayIso();
  const defaultTomorrow = getStableTomorrowIso();

  return {
    locationId: q.locationId ?? "",
    search: q.search ?? "",
    lat: q.lat ?? "",
    lng: q.lng ?? "",
    start: q.start ?? defaultToday,
    end: q.start && !q.end ? "" : (q.end ?? defaultTomorrow),
    vehicleType: q.vehicleType ?? "motorbike",
    pickupType: q.pickupType ?? "1",

    priceRange: q.priceRange ?? [],
    gear: q.gear ?? [],
    engine: q.engine ?? [],
    size: q.size ?? [],
    category: q.category ?? [],
    mustInclude: q.mustInclude ?? [],
    rating: q.rating ?? [],

    setLocationId,
    setSearch,
    setCoordinates,
    setRange,
    setDate,
    setVehicleType,
    setPickupType,
    setFilters,
    clearSearch,
    clearFilters,
  };
};

interface Rental {
  id: string | number;
  title?: string;
  name?: string;
  provinceId?: number | null;
}

export function useRentalHref(rental: Rental, href?: string): string {
  const { start, vehicleType, pickupType, lat, lng } = useRentalQuery();

  return useMemo(() => {
    if (href) return href;

    const query = buildQueryString({
      search: rental.title ?? rental.name,
      start,
      vehicleType,
      pickupType,
      locationId: rental.provinceId,
      lat: lat && lng ? lat : undefined,
      lng: lat && lng ? lng : undefined,
    });

    return `/rentals/${rental.id}${query ? `?${query}` : ""}`;
  }, [
    href,
    rental.id,
    rental.title,
    rental.name,
    start,
    vehicleType,
    pickupType,
    rental.provinceId,
    lat,
    lng,
  ]);
}
